import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import cn from 'classnames';
import { BottomSheetContentLayout, useIsInBottomSheet } from '../BottomSheet';
import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import { useDevice } from '../Device';
import { Typography } from '../Typography';
import styles from './ConfirmationModal.module.scss';
import { ConfirmationModalTestIds } from './ConfirmationModal.utils';
export function ConfirmationModal(props) {
    var _ref, _ref1, _ref2;
    const { title, content, body, className, contentClassName, bodyClassName, footerClassName, headerClassName, contentRef, skin = 'primary', cancelButtonDisabled, primaryButtonDisabled, primaryButtonLabel, primaryButtonTestId, primaryButtonTooltip, primaryButtonLoading, primaryButtonIntent, primaryButtonFormId, secondaryButtonLabel, secondaryButtonTestId, secondaryButtonLoading, width = 590, height, maxHeight, textAlignCenter = true, as = 'div', contentPadding = 'normal', overflowContent = false, onPrimaryButtonClick, onSecondaryButtonClick, onCloseButtonClick, testId, bi, style } = props, rest = _object_without_properties(props, [
        "title",
        "content",
        "body",
        "className",
        "contentClassName",
        "bodyClassName",
        "footerClassName",
        "headerClassName",
        "contentRef",
        "skin",
        "cancelButtonDisabled",
        "primaryButtonDisabled",
        "primaryButtonLabel",
        "primaryButtonTestId",
        "primaryButtonTooltip",
        "primaryButtonLoading",
        "primaryButtonIntent",
        "primaryButtonFormId",
        "secondaryButtonLabel",
        "secondaryButtonTestId",
        "secondaryButtonLoading",
        "width",
        "height",
        "maxHeight",
        "textAlignCenter",
        "as",
        "contentPadding",
        "overflowContent",
        "onPrimaryButtonClick",
        "onSecondaryButtonClick",
        "onCloseButtonClick",
        "testId",
        "bi",
        "style"
    ]);
    const [loading, setLoading] = useState(false);
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const Tag = as;
    const showFooter = !!secondaryButtonLabel || !!primaryButtonLabel;
    function handlePrimaryButtonClick(event) {
        if (!onPrimaryButtonClick) return;
        const result = onPrimaryButtonClick(event);
        if (result instanceof Promise) {
            setLoading(true);
            result.finally(()=>setLoading(false));
        }
    }
    return isInBottomSheet ? /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        contentClassName: styles.bottomSheetContent,
        header: {
            title,
            paddingBottom: 8
        },
        footer: {
            paddingTop: 8,
            // Not secondary button on purpose. Its usually a cancel button and we already have a close button.
            primaryAction: primaryButtonLabel ? {
                text: primaryButtonLabel,
                onClick: handlePrimaryButtonClick,
                disabled: primaryButtonDisabled,
                intent: primaryButtonIntent,
                testId: primaryButtonTestId !== null && primaryButtonTestId !== void 0 ? primaryButtonTestId : ConfirmationModalTestIds.primaryButton,
                loading: primaryButtonLoading || loading,
                tooltip: primaryButtonTooltip ? {
                    placement: 'top',
                    title: primaryButtonTooltip
                } : undefined,
                bi: {
                    name: (_ref = bi === null || bi === void 0 ? void 0 : bi.primaryButton.name) !== null && _ref !== void 0 ? _ref : '',
                    data: bi === null || bi === void 0 ? void 0 : bi.primaryButton.data
                }
            } : undefined
        },
        children: content
    }) : /*#__PURE__*/ _jsxs(Tag, _object_spread_props(_object_spread({
        className: cn(styles.root, className)
    }, rest), {
        "data-testid": testId !== null && testId !== void 0 ? testId : ConfirmationModalTestIds.root,
        style: _object_spread({
            width,
            height,
            maxHeight
        }, style),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.header, styles["".concat(skin, "Skin")], headerClassName),
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        weight: "bold",
                        variant: "h6",
                        testId: ConfirmationModalTestIds.title,
                        children: title
                    }),
                    /*#__PURE__*/ _jsx(CloseButton, {
                        skin: "transparent",
                        onClick: onCloseButtonClick || onSecondaryButtonClick,
                        className: styles.closeButton,
                        testId: ConfirmationModalTestIds.closeButton,
                        disabled: !!cancelButtonDisabled
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.body, {
                    [styles.infiniteBodyPadding]: overflowContent
                }, bodyClassName),
                children: [
                    body,
                    /*#__PURE__*/ _jsx("div", {
                        ref: contentRef,
                        "data-testid": ConfirmationModalTestIds.content,
                        className: cn(styles.content, {
                            [styles.contentAlignmentCenter]: textAlignCenter,
                            [styles.contentSmallPadding]: contentPadding === 'small',
                            [styles.mobile]: isMobileDevice
                        }, contentClassName),
                        children: typeof content === 'string' ? /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            children: content
                        }) : content
                    })
                ]
            }),
            showFooter && /*#__PURE__*/ _jsx("div", {
                className: cn(styles.footer, footerClassName),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles.actions,
                    children: [
                        secondaryButtonLabel && /*#__PURE__*/ _jsx(Button, {
                            size: "medium",
                            skin: skin === 'transparent' ? 'primaryOutline' : "".concat(skin, "Outline"),
                            bi: {
                                name: (_ref1 = bi === null || bi === void 0 ? void 0 : bi.secondaryButton.name) !== null && _ref1 !== void 0 ? _ref1 : '',
                                data: bi === null || bi === void 0 ? void 0 : bi.secondaryButton.data
                            },
                            onClick: onSecondaryButtonClick,
                            testId: secondaryButtonTestId !== null && secondaryButtonTestId !== void 0 ? secondaryButtonTestId : ConfirmationModalTestIds.secondaryButton,
                            loading: secondaryButtonLoading,
                            type: "button",
                            children: secondaryButtonLabel
                        }),
                        primaryButtonLabel && /*#__PURE__*/ _jsx(Button, {
                            type: "submit",
                            size: "medium",
                            form: primaryButtonFormId,
                            skin: skin === 'transparent' ? 'primary' : "".concat(skin),
                            intent: primaryButtonIntent,
                            bi: {
                                name: (_ref2 = bi === null || bi === void 0 ? void 0 : bi.primaryButton.name) !== null && _ref2 !== void 0 ? _ref2 : '',
                                data: bi === null || bi === void 0 ? void 0 : bi.primaryButton.data
                            },
                            onClick: handlePrimaryButtonClick,
                            testId: primaryButtonTestId !== null && primaryButtonTestId !== void 0 ? primaryButtonTestId : ConfirmationModalTestIds.primaryButton,
                            loading: primaryButtonLoading || loading,
                            disabled: primaryButtonDisabled,
                            tooltip: primaryButtonTooltip ? {
                                placement: 'top',
                                title: primaryButtonTooltip
                            } : undefined,
                            children: primaryButtonLabel
                        })
                    ]
                })
            })
        ]
    }));
}
